import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { createDSR } from '@/services/db/dsrEntry';

export default function NewDSRPage() {
  const navigate = useNavigate();
  const tenantId = useAuthStore((s) => s.tenantId);
  const projectId = useAuthStore((s) => s.projectId);
  const userId = useAuthStore((s) => s.user?.id);
  const userRole = useAuthStore((s) => s.userRole);
  const ranRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ranRef.current) return;
    if (!tenantId || !projectId || !userId) return;
    if (userRole !== 'ssr') {
      setError('Only an SSR can start a new DSR.');
      return;
    }
    ranRef.current = true;
    createDSR({ tenantId, projectId, createdBy: userId })
      .then((dsrId) => navigate(`/dsr/${dsrId}/edit`, { replace: true }))
      .catch((err: Error) => setError(err.message || 'Could not create DSR'));
  }, [tenantId, projectId, userId, userRole, navigate]);

  if (error) {
    return (
      <div className="mx-auto max-w-xl space-y-4 py-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to dashboard
        </Button>
        <Card>
          <CardContent className="flex items-start gap-3 py-6">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-mozaic-orange" />
            <div className="space-y-1">
              <p className="font-medium">Could not start a new report</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-muted-foreground">
      <Loader2 className="h-8 w-8 animate-spin text-mozaic-teal" />
      <p className="text-sm">Starting today's report…</p>
    </div>
  );
}
